import { useRef } from 'react';
import { isEmbeddable } from '../constants/partners';
import device, { getPermissionBlockedDialogKeys } from '../utils/device';
import { logInfo, logWarning } from '../utils/remoteLogger';
import {
  MEDIA_PERMISSION_DENIED_EVENT,
  postWindowEvent,
} from '../utils/windowEvents';
import useLanguage from './useLanguage';
import useOverlay from './useOverlay';

type MediaStreamConstraints = {
  audio?: boolean | MediaTrackConstraints;
  video?: boolean | MediaTrackConstraints;
  isPreemptive?: boolean;
};

type PermissionStateLike = 'granted' | 'denied' | 'prompt';

type PermissionEventCallback = (
  state: Exclude<PermissionStateLike, 'prompt'>
) => void;

const useMediaDevicePermission = () => {
  const language = useLanguage();
  const overlay = useOverlay();

  // Refs to prevent stale closures
  const languageRef = useRef(language);
  languageRef.current = language;

  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  const MEDIA_PERMISSION_CACHE_KEY = 'media_permission_state';

  const canUsePermissionsApi = () =>
    typeof navigator !== 'undefined' &&
    !!(navigator as any).permissions &&
    typeof (navigator as any).permissions.query === 'function';

  const queryPermission = async (
    name: 'microphone' | 'camera'
  ): Promise<PermissionStateLike> => {
    try {
      const status = await (navigator as any).permissions.query({ name });
      return status.state as PermissionStateLike;
    } catch {
      return 'prompt';
    }
  };

  const getCachedPermissionState = (): PermissionStateLike | null => {
    try {
      const cached = localStorage.getItem(MEDIA_PERMISSION_CACHE_KEY);
      if (cached === 'granted' || cached === 'denied') return cached;
      return null;
    } catch {
      return null;
    }
  };

  const setCachedPermissionState = (state: 'granted' | 'denied'): void => {
    try {
      localStorage.setItem(MEDIA_PERMISSION_CACHE_KEY, state);
    } catch {
      // ignore (private mode / storage blocked)
    }
  };

  const clearCachedPermissionState = (): void => {
    try {
      localStorage.removeItem(MEDIA_PERMISSION_CACHE_KEY);
    } catch {
      // ignore (private mode / storage blocked)
    }
  };

  /**
   * Permission check across platforms.
   * - Desktop/Android: Supports the navigator.permissions.query API.
   * - iOS: iOS Safari (and Chrome on iOS) does not support the Permissions API for camera and microphone.
   *    It is impossible for a website to check if permission is "denied" without actually trying to open the camera.
   *
   * NOTE: On iOS, if this runs without a user gesture, the probe may fail even if user would allow it.
   *
   * IMPORTANT CHANGE:
   * - In NON-preemptive mode (e.g., "Accept call"), we never short-circuit based on cached/known "denied".
   *   We always allow a real getUserMedia attempt so the browser can prompt again (or succeed after settings change).
   */
  const checkPermission = async (
    constraints: MediaStreamConstraints
  ): Promise<PermissionStateLike> => {
    // iOS: Permissions API not reliable/available; use cache carefully.
    if (device.IS_IOS) {
      const cachedState = getCachedPermissionState();

      // If cached as granted, return granted for both preemptive and non-preemptive
      // This avoids unnecessary getUserMedia calls that can cause camera "paused" state
      if (cachedState === 'granted') return 'granted';

      // Preemptive: respect cached denied to avoid re-prompt UX
      if (constraints.isPreemptive && cachedState === 'denied') return 'denied';

      // Non-preemptive: never block based on cached denied; allow real attempt
      return 'prompt';
    }

    // Non-iOS: Prefer Permissions API if available.
    if (canUsePermissionsApi()) {
      const wantAudio = !!constraints.audio;
      const wantVideo = !!constraints.video;

      const mic = wantAudio ? await queryPermission('microphone') : 'granted';
      const cam = wantVideo ? await queryPermission('camera') : 'granted';

      if (mic === 'granted' && cam === 'granted') return 'granted';

      // Preemptive: if either is denied, short-circuit to denied (no prompt/UI desired)
      if (constraints.isPreemptive && (mic === 'denied' || cam === 'denied'))
        return 'denied';

      // Non-preemptive: do not short-circuit to denied; allow real attempt
      return 'prompt';
    }

    // Fallback: cache (but do not short-circuit denied in non-preemptive mode)
    const cachedState = getCachedPermissionState();
    if (cachedState === 'granted') return 'granted';
    if (constraints.isPreemptive && cachedState === 'denied') return 'denied';
    return 'prompt';
  };

  const getMediaDevicePermission = (
    constraints: MediaStreamConstraints,
    onPermissionEvent?: PermissionEventCallback
  ) => {
    const currentLanguage = languageRef.current;
    const currentOverlay = overlayRef.current;

    return new Promise<boolean>((resolve) => {
      const requestPermission = (isRetry?: boolean) => {
        navigator.mediaDevices
          ?.getUserMedia(constraints)
          .then((mediaStream) => {
            // Cache success to avoid re-prompt UX on iOS after reloads.
            setCachedPermissionState('granted');

            // Immediately stop all tracks, as we're not using them directly.
            mediaStream.getTracks().forEach((t) => t.stop());

            onPermissionEvent?.('granted');

            if (isRetry) {
              logInfo(
                'useMediaDevicePermission',
                `Media device permission granted after a retry`
              );
            }

            currentOverlay.dismiss();
            resolve(true);
          })
          .catch((error) => {
            const isDenied =
              error?.name === 'NotAllowedError' ||
              error?.name === 'PermissionDeniedError';

            if (isDenied) {
              onPermissionEvent?.('denied');
            }

            // Only cache denied when it truly looks like a permission denial
            if (isDenied && !constraints.isPreemptive)
              setCachedPermissionState('denied');

            if (constraints.isPreemptive) {
              // Preemptive: no UI, no blocking
              logWarning(
                'useMediaDevicePermission',
                `Preemptive media device permission has been denied by user`,
                error
              );
              resolve(false);
              return;
            }

            if (!constraints.isPreemptive) {
              if (isDenied) postWindowEvent(MEDIA_PERMISSION_DENIED_EVENT);

              const { titleKey, messageKey, buttonKey } =
                getPermissionBlockedDialogKeys('try_again');

              currentOverlay.presentAlert({
                title: currentLanguage.get(titleKey),
                message: currentLanguage.get(messageKey),
                items: [
                  {
                    title: currentLanguage.get(buttonKey),
                    onClick: () => {
                      // Clear cache before retry to allow user to grant permission
                      clearCachedPermissionState();
                      // Ensures user gesture for retry
                      requestPermission(true);
                    },
                  },
                ],
              });

              logWarning(
                'useMediaDevicePermission',
                `Media device permission has been denied`,
                error
              );
            }
          });
      };

      checkPermission(constraints).then((state) => {
        if (state === 'granted') {
          // Trust granted state to avoid unnecessary probes/prompts
          onPermissionEvent?.('granted');
          resolve(true);
          return;
        }

        if (state === 'denied') {
          // Preemptive: keep silent behavior
          if (constraints.isPreemptive) {
            logWarning(
              'useMediaDevicePermission',
              `Preemptive media device permission has been denied by user (cached or Permissions API)`
            );
            resolve(false); // Resolve silently
            return;
          }
          /**
           * Non-preemptive (e.g., "Accept call"):
           * Show custom prompt first to get user gesture, then attempt getUserMedia.
           * This allows the browser to prompt again or succeed if the user changed settings.
           */
          clearCachedPermissionState();
          currentOverlay.presentAlert({
            title: currentLanguage.get(
              'patient_web.media_device_permission.query.title'
            ),
            message: currentLanguage.get(
              'patient_web.media_device_permission.query.message'
            ),
            items: [
              {
                title: currentLanguage.get('ok'),
                onClick: () => {
                  currentOverlay.dismiss();
                  requestPermission();
                },
              },
            ],
          });
          return;
        }

        // If state is 'prompt'
        if (state === 'prompt') {
          // An embeddable partner's app grants camera and microphone to its web view itself, so
          // ask right away. If that fails, requestPermission shows the blocked dialog with a retry.
          if (
            constraints.isPreemptive ||
            (isEmbeddable() && device.IS_IN_APP_WEBVIEW)
          ) {
            requestPermission();
            return;
          }

          // For non-preemptive, if the state is 'prompt',
          // we show a custom dialog to get a user gesture before triggering the native prompt.
          currentOverlay.presentAlert({
            title: currentLanguage.get(
              'patient_web.media_device_permission.query.title'
            ),
            message: currentLanguage.get(
              'patient_web.media_device_permission.query.message'
            ),
            items: [
              {
                title: currentLanguage.get('ok'),
                onClick: () => {
                  currentOverlay.dismiss();
                  requestPermission();
                },
              },
            ],
          });
          return;
        }

        // Fallback for any unexpected state, though 'granted', 'denied', 'prompt' should cover all.
        // If we reach here, it's an unexpected state, just try to request permission.
        requestPermission();
      });
    });
  };

  return useRef({
    getMediaDevicePermission,
    clearPermissionCache: clearCachedPermissionState,
  }).current;
};

export default useMediaDevicePermission;
